import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { ChevronDown, ChevronUp } from '../../icons';
import type { TaskDto } from '../../api/tasks';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
import { TeamGantt } from './TeamGantt';
// 2026-08-26 — 문장뿐이던 요약 바에 상태 분포 한 줄. 신규 파일(도크 구조 무수정).
import { StatusMiniBar } from './task-viz';
import './TaskBoardShell.css';

interface TeamProgressDockProps {
  tasks: TaskDto[];
  teamId: string | null;
  /** 진행 현황 목록에서 태스크를 누르면 상세로 — 누른 뒤 도크는 닫는다. */
  onOpenTask?: (task: TaskDto) => void;
}

/**
 * TeamProgressDock (2026-08-18 시안 ①③) — 팀 진행 현황을 **보드 하단 바**로 접는다.
 *
 * 종전에는 `TeamGantt` 가 칸반 위에 늘 펼쳐져 카드가 보이는 높이를 잡아먹었다.
 * 기본 = 한 줄 요약 바(팀원 N명 · 진행중 N건), 탭하면 배경을 딤 처리한 오버레이로 펼쳐
 * 기존 간트를 **그대로** 얹는다 — TeamGantt 는 한 줄도 고치지 않는다(래핑만).
 *
 * 접힘/펼침은 화면 상태라 저장하지 않는다(진입 시 항상 접힘 = 시안의 기본 상태).
 */
export function TeamProgressDock({ tasks, teamId, onOpenTask }: TeamProgressDockProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const [open, setOpen] = useState(false);

  // 요약 수치 — TeamGantt 와 같은 기준(담당자 있는 태스크의 고유 담당자 수 · 진행중 건수).
  const { memberCount, inProgress } = useMemo(() => {
    const ids = new Set<string>();
    let ip = 0;
    for (const t of (Array.isArray(tasks) ? tasks : [])) {
      for (const uid of (Array.isArray(t.assignees) ? t.assignees : [])) if (uid) ids.add(uid);
      if (t.status === 'IN_PROGRESS') ip += 1;
    }
    return { memberCount: ids.size, inProgress: ip };
  }, [tasks]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const summary = tTask('task.dock.summary', { m: String(memberCount), n: String(inProgress) });

  return (
    <>
      {open && (
        <>
          <div className="tg-dock-scrim" onClick={() => setOpen(false)} aria-hidden="true" />
          <section className="tg-dock-panel" aria-label={summary}>
            <div className="tg-dock-panel-body scrollbar-thin">
              <TeamGantt
                tasks={tasks}
                teamId={teamId}
                {...(onOpenTask
                  ? { onOpenTask: (t) => { setOpen(false); onOpenTask(t); } }
                  : {})}
              />
            </div>
            <button
              type="button"
              className="tg-dock-toggle tg-dock-toggle--in"
              aria-expanded={true}
              onClick={() => setOpen(false)}
            >
              <ChevronDown size={13} aria-hidden />
              {tTask('task.dock.collapse')}
            </button>
          </section>
        </>
      )}

      {/* 펼친 동안에는 요약 바를 감춘다 — 시안의 펼친 상태에는 패널 하단의 [접기] 하나만 있다. */}
      {!open && (
        <button
          type="button"
          className="tg-dock-bar"
          aria-expanded={false}
          onClick={() => setOpen(true)}
        >
          <span className="tg-dock-summary">{summary}</span>
          {/* 이 프로젝트가 시작 단계인지 마무리 단계인지 — 문장으로는 안 보이던 것. */}
          <StatusMiniBar tasks={tasks} />
          <span className="tg-dock-cta">
            <ChevronUp size={13} aria-hidden />
            {tTask('task.dock.expand')}
          </span>
        </button>
      )}
    </>
  );
}
